import { useCallback, useEffect, useReducer, useState } from "react";
import Form from "@src/components/forms/Form";
import { Checkbox, DropDown, FormInput, Loader } from "@src/components/atoms";
import Button from "@src/components/atoms/Button";
import moment from "moment";
import { teamSchema } from "../formHelpers";
import {
  normalizeEmptyStringsToNull,
  getEventTypeIdFromIndustry,
} from "./AdminHelpers";

export const TeamForm = ({
  isNew = false,
  industry,
  data = {},
  onSubmit,
  onCancel,
}) => {
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [minBSRDate, setMinBSRDate] = useState(null);
  const [maxBSRDate, setMaxBSRDate] = useState(null);

  const initialTeamObject = {
    name: data?.name || "",
    alias: data?.alias || "",
    baselineSuccessRateStartDate: data?.baselineSuccessRateStartDate || "",
    baselineSuccessRateEndDate: data?.baselineSuccessRateEndDate || "",
    baselineEventCountEndDate: data?.baselineEventCountEndDate || "",
    isDefault: data?.isDefault || false,
    eventTypeId:
      data?.eventTypeId || getEventTypeIdFromIndustry(industry) || "",
    //more fields
  };

  const handleSubmit = async (data) => {
    console.log("form data", data);
    setSubmitting(true);
    setError(null);

    try {
      const cleanedData = normalizeEmptyStringsToNull(data);
      onSubmit && (await onSubmit(cleanedData));
    } catch (err) {
      setError("Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isNew && !data.name) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col overflow-auto">
      <Form
        onSubmit={(data) => {
          handleSubmit(data);
        }}
        schema={teamSchema}
        options={{ defaultValues: initialTeamObject }}
        className="space-y-6"
      >
        {(methods) => {
          const hasChanged = methods.formState.isDirty;
          return (
            <>
              <div className="space-y-4">
                <FormInput
                  label="Team Code Name"
                  info="Must exactly match 'team_name' in databricks code"
                  name="name"
                  value={methods.watch("name")}
                  onChange={(e) => {
                    methods.setValue("name", e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                />
                <FormInput
                  label="Team Alias"
                  name="alias"
                  value={methods.watch("alias")}
                  onChange={(e) => {
                    methods.setValue("alias", e.target.value, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                />
                <Checkbox
                  type="other"
                  id="isDefault"
                  name="isDefault"
                  key="isDefault"
                  fontSize="m"
                  label="Is Default"
                  checked={methods.watch("isDefault")}
                  onChange={(e) => {
                    methods.setValue("isDefault", e.target.checked, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                />
                <FormInput
                  label="Event Type ID"
                  name="eventTypeId"
                  value={methods.watch("eventTypeId")}
                  readOnly={true}
                />
                <div className="border-1 rounded-sm border-gray-500/50 border-solid p-4">
                  <h6 className="mb-4 text-red-400">
                    Changing these can impact historical and current goals for
                    operators in this team.
                    <br />
                    Changes will not take effect until new data is processed
                    (~5am daily except for a full customer data outage).
                  </h6>
                  <FormInput
                    label="Custom Baseline Sucess Rate Start Date"
                    name="baselineSuccessRateStartDate"
                    type="date"
                    maxDate={maxBSRDate}
                    fValue={moment
                      .utc(methods.watch("baselineSuccessRateStartDate"))
                      .format("YYYY-MM-DD")}
                    onChange={(e) => {
                      setMinBSRDate(e.target.value);
                      methods.setValue(
                        "baselineSuccessRateStartDate",
                        e.target.value,
                        {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        },
                      );
                    }}
                  />
                  <FormInput
                    label="Custom Baseline Sucess Rate End Date"
                    name="baselineSuccessRateEndDate"
                    type="date"
                    minDate={minBSRDate}
                    fValue={moment
                      .utc(methods.watch("baselineSuccessRateEndDate"))
                      .format("YYYY-MM-DD")}
                    onChange={(e) => {
                      setMaxBSRDate(e.target.value);
                      methods.setValue(
                        "baselineSuccessRateEndDate",
                        e.target.value,
                        {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        },
                      );
                    }}
                  />
                  <FormInput
                    label="Custom Baseline Event Count End Date"
                    name="baselineEventCountEndDate"
                    type="date"
                    fValue={moment
                      .utc(methods.watch("baselineEventCountEndDate"))
                      .format("YYYY-MM-DD")}
                    onChange={(e) => {
                      methods.setValue(
                        "baselineEventCountEndDate",
                        e.target.value,
                        {
                          shouldValidate: true,
                          shouldDirty: true,
                          shouldTouch: true,
                        },
                      );
                    }}
                  />
                </div>
              </div>
              <Button submit loading={submitting}>
                Save changes
              </Button>
              {error && (
                <div className="mt-4 text-xs italic text-red-500">{error}</div>
              )}
            </>
          );
        }}
      </Form>{" "}
    </div>
  );
};
